import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { message } from 'ant-design-vue';
import { useLogin } from '/@/hooks/web/useLogin';
import { ContentTypeEnum } from '/@/enums/httpEnum';
import { PlatformSource } from '/@/enums/statusEnum';
import { useUserStore } from '../store/modules/user';
import { getApiPrefix } from '../libs/config';
import { AxiosCanceler } from './cancel';

const canceler = new AxiosCanceler();

// 重定向到登录页面
const redirectLogin = () => {
  const userStore = useUserStore();
  userStore.clearState();
  userStore.clearUserInfo();
  const { login } = useLogin();
  login();
};

const showError = (msg: string, showMessage = true) => {
  showMessage && msg && message.error(msg);
};

const errorHandler = (status: number, statusText: string, showMessage: boolean = true) => {
  switch (status) {
    case 400:
      showError('请求错误', showMessage);
      break;
    case 10101:
      // router.push({ path: '/404' });
      break;
    case 401:
      showError('未登录或者登录状态已失效', showMessage);
      redirectLogin();
      break;
    case 40004:
      // showError(statusText || '登录状态失效，请重新登录');
      redirectLogin();
      break;
    case 403:
      showError('权限不足，请联系管理员', showMessage);
      break;
    case 404:
      showError('接口不存在', showMessage);
      break;
    case 500:
    case 502:
      showError(statusText || '系统繁忙，请联系管理员', showMessage);
      break;
    case 503:
      showError('系统正在升级，请稍后', showMessage);
      break;
    default:
      showError(statusText, showMessage);
      break;
  }
};

const axiosInstance = axios.create({
  baseURL: getApiPrefix(),
  timeout: 600000,
  withCredentials: false,
  headers: {
    'Content-Type': ContentTypeEnum.JSON,
  },
});

// "multipart/form-data;charset=UTF-8" 格式处理
const formDataInterceptors = (config: AxiosRequestConfig) => {
  if (config.data) {
    const formData = new window.FormData();
    Object.keys(config.data).forEach((key) => {
      if (!config.data) return;
      const value = config.data[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(`${key}[]`, item);
        });
        return;
      }
      formData.append(key, config.data[key]);
    });
    config.data = formData;
  }
};

// 请求前置拦截器
axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const userStore = useUserStore();
    config.headers.source = PlatformSource.web;
    if (config.canCancel) {
      canceler.addPending(config);
    }
    if (config.requireLogin !== false && userStore.getToken) {
      config.headers.Authorization = `Bearer ${userStore.getToken}`;
    }
    if (userStore.getCompanyId) {
      config.headers['company-id'] = userStore.getCompanyId;
    }

    if (config.headers['Content-Type'] === ContentTypeEnum.FORM_DATA) {
      formDataInterceptors(config);
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const showMessage = (response.config as any).message !== false;
    if (response.data?.code) {
      // code 不为 0 认为是错误
      const { msg, code } = response.data;
      errorHandler(code || status, msg, showMessage);
      return Promise.reject({ msg, code: code || status });
    }

    if (response.config.headers.returnAll) {
      return response;
    }

    return response.data.data;
  },
  (error: AxiosError) => {
    if (error.response) {
      const showMessage = (error.config as any).message !== false;
      const { status, statusText, data } = error.response;
      errorHandler(data?.code || status, data?.msg || statusText, showMessage);

      return Promise.reject({
        code: data?.code || status,
        msg: data ? data.msg : statusText,
      });
    }

    return Promise.reject(error);
  }
);

export default {
  request: axiosInstance,

  get: <T>(path: string, data: Recordable = {}, config = {}): Promise<T> => {
    return axiosInstance.get(path, {
      params: data,
      ...config,
    });
  },

  post: <T>(path: string, data: Recordable = {}, config = {}): Promise<T> => {
    return axiosInstance.post(path, data, config);
  },

  put: <T>(path: string, data: Recordable = {}, config = {}): Promise<T> =>
    axiosInstance.put(path, data, config),

  delete: <T>(path: string, data: Recordable = {}, config = {}): Promise<T> =>
    axiosInstance.delete(path, {
      params: data,
      ...config,
    }),
};
